import React from 'react'
import { Link } from 'react-router-dom'
import StaticCmsPage, { StaticSection } from '../../components/nemonto/StaticCmsPage'

// The policy text on its own - also shown in a modal from the signup
// consent line (LegalModal).
export const PrivacyPolicyContent = () => (
    <>
        <StaticSection>
            <p><strong>Last updated: November 3, 2026</strong></p>
            <p>
                This Privacy Policy explains how Nemonto ("Nemonto", "we", "us" or "our") collects, uses, shares and
                protects your information when you use the Nemonto website at{' '}
                <a href="https://nemonto.com/" target="_blank" rel="noopener noreferrer">nemonto.com</a> and the Nemonto
                mobile apps for iOS and Android (together, the "Service"). It also explains the choices and rights you have.
            </p>
            <p>
                By using the Service, you agree to the practices described here. If you don't agree, please don't use
                the Service. This policy should be read together with our <Link to="/terms-of-use">Terms of Use</Link>.
            </p>
        </StaticSection>

        <StaticSection heading="1. Information we collect">
            <h3>Information you give us</h3>
            <ul>
                <li><strong>Account details:</strong> your first and last name, email address and/or phone number, password, date of birth and gender. We verify your email or phone number with a one-time code.</li>
                <li><strong>Profile details:</strong> your profile photo, bio, address or city, and the food categories and cuisines you choose as interests.</li>
                <li><strong>Content you post:</strong> recipes (ingredients, steps, cook time, calories), posts, stories, photos, videos, "Made it" entries, challenge entries and challenges you host, comments, reactions, captions and the people you tag.</li>
                <li><strong>Messages:</strong> messages and media you send in direct and group chats.</li>
                <li><strong>Ad posts and payments:</strong> if you create an ad post, the ad content and billing details. Card payments are processed by our payment provider, Stripe. We don't store your full card number.</li>
                <li><strong>Support requests:</strong> what you tell us when you use the Contact Us form or otherwise get in touch.</li>
            </ul>

            <h3>Information created when you use the Service</h3>
            <ul>
                <li><strong>Activity:</strong> who you follow and who follows you, friend requests, blocked accounts, likes, favourites, saved recipes, challenge participation, badges, and the notifications you receive.</li>
                <li><strong>Location:</strong> if you allow it, a location you attach to a post or your profile. You can choose not to share location.</li>
                <li><strong>Device and usage data:</strong> IP address, device type and identifiers, operating system, browser type, app version, pages and screens you view, the date and time of your visits, and diagnostic data such as crash logs.</li>
                <li><strong>Push notification tokens:</strong> if you turn on notifications, a token that lets us send them to your device.</li>
            </ul>

            <h3>Cookies and similar technologies</h3>
            <p>
                The website uses cookies and browser storage to keep you signed in, remember your preferences, secure
                your account and understand how the Service is used. You can block or delete cookies in your browser
                settings, but some features (such as staying signed in) may stop working.
            </p>
        </StaticSection>

        <StaticSection heading="2. How we use your information">
            <ul>
                <li>To create and run your account, and to verify your identity when you sign up or sign in.</li>
                <li>To show your recipes, posts, stories and profile to other users, according to your settings.</li>
                <li>To personalise your feed and suggest recipes, challenges and people you may know, based on your interests and connections.</li>
                <li>To run challenges, including showing entries, letting hosts pick winners and awarding badges.</li>
                <li>To deliver messages, group chats and notifications.</li>
                <li>To process ad posts and payments.</li>
                <li>To respond to your questions and support requests.</li>
                <li>To keep the Service safe: preventing spam, fraud and abuse, and enforcing our <Link to="/terms-of-use">Terms of Use</Link>.</li>
                <li>To measure and improve the Service and fix problems.</li>
                <li>To meet our legal obligations.</li>
            </ul>
            <p><strong>We do not sell your personal information.</strong></p>
        </StaticSection>

        <StaticSection heading="3. What other users can see">
            <p>
                Nemonto is a social platform. Your name, profile photo, bio, and the content you post (recipes, posts,
                stories, "Made it" and challenge entries, comments) can be seen by other users and, for public content,
                by visitors to the website. Your followers and following lists and badges can also be visible.
            </p>
            <p>
                You can block other users, delete content you've posted, and use the privacy options available in your
                account settings. Messages in a chat are visible to the people in that chat.
            </p>
        </StaticSection>

        <StaticSection heading="4. How we share information">
            <p>We share information only in these cases:</p>
            <ul>
                <li><strong>With other users,</strong> as described above.</li>
                <li><strong>With service providers</strong> who help us run the Service, such as cloud hosting and media storage (Amazon Web Services), payment processing (Stripe), and push notifications (Google Firebase). They may only use your information to provide services to us.</li>
                <li><strong>With our technology partners</strong> Syntch and Debtech LLC, who build and operate the Service on our behalf.</li>
                <li><strong>For legal reasons,</strong> if we believe it is required by law or legal process, or needed to protect the rights, safety or property of our users, the public or Nemonto.</li>
                <li><strong>In a business transfer,</strong> such as a merger, acquisition or sale of assets, in which case this policy will continue to apply to your information.</li>
                <li><strong>With your consent,</strong> or at your direction.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="5. How long we keep information">
            <p>
                We keep your information for as long as your account is active and as needed to provide the Service.
                When you delete content, we remove it from the Service, though copies may remain in backups for a limited time.
            </p>
            <p>
                You can delete your account at any time from the app's settings. When you do, we delete or anonymise your
                personal information, except where we need to keep some of it to meet legal obligations (for example,
                payment records), resolve disputes or enforce our terms.
            </p>
        </StaticSection>

        <StaticSection heading="6. Your choices and rights">
            <ul>
                <li><strong>Access and update:</strong> view and edit your profile information at any time in your profile settings.</li>
                <li><strong>Privacy controls:</strong> block other users and use the privacy options in your account settings.</li>
                <li><strong>Notifications:</strong> turn push notifications off in your device or app settings.</li>
                <li><strong>Location:</strong> turn off location sharing in your device settings.</li>
                <li><strong>Delete:</strong> delete individual posts, or your whole account.</li>
            </ul>
            <p>
                Depending on where you live, you may have additional rights, such as the right to request a copy of your
                data, to correct or delete it, to object to or restrict certain processing, or to withdraw consent.
                Residents of some U.S. states, including Virginia and California, have rights under their state privacy
                laws. To make a request, <Link to="/contact-us">contact us</Link>. We may need to verify your identity before
                acting on it, and we won't discriminate against you for exercising your rights.
            </p>
        </StaticSection>

        <StaticSection heading="7. Security">
            <p>
                We use reasonable administrative, technical and physical safeguards to protect your information,
                including encrypted connections (HTTPS), access controls and secure cloud storage. No method of
                transmission or storage is completely secure, so we can't guarantee absolute security. Please use a
                strong password and keep it to yourself.
            </p>
        </StaticSection>

        <StaticSection heading="8. Children's privacy">
            <p>
                Nemonto is not intended for children under 13, and we don't knowingly collect personal information from
                children under 13. If you're a parent or guardian and believe your child has given us personal
                information, please <Link to="/contact-us">contact us</Link> and we will delete it.
            </p>
        </StaticSection>

        <StaticSection heading="9. International users">
            <p>
                Nemonto is operated from the United States. If you use the Service from outside the U.S., your
                information will be transferred to, stored and processed in the United States, where data protection
                laws may differ from those in your country.
            </p>
        </StaticSection>

        <StaticSection heading="10. Third-party links">
            <p>
                The Service may contain links to other websites or services, including in user posts and ad posts. We're
                not responsible for their privacy practices, so please read their privacy policies.
            </p>
        </StaticSection>

        <StaticSection heading="11. Changes to this policy">
            <p>
                We may update this Privacy Policy from time to time. When we do, we'll change the "Last updated" date
                above, and for significant changes we'll let you know through the Service or by email.
            </p>
        </StaticSection>

        <StaticSection heading="12. Contact us">
            <p>
                If you have questions about this Privacy Policy or how we handle your information, please reach us
                through our <Link to="/contact-us">Contact Us</Link> page.
            </p>
        </StaticSection>
    </>
)

const PrivacyPolicy = () => (
    <StaticCmsPage slug="privacy-policy" title="Privacy Policy" subtitle="How we collect, use, and protect your information.">
        <PrivacyPolicyContent />
    </StaticCmsPage>
)

export default PrivacyPolicy
